import type { CustomDataMap, DatapointStatus, PredefinedCheckpoint } from "owa-analytics-types";
import { type UTPerfDatapointStatus, type UTCommonPerfProperties } from "@outlook/unified_telemetry";

const checkpointValues: ReadonlyArray<PredefinedCheckpoint> = [
    "Code_S",
    "Code_E",
    "Data_S",
    "Data_E",
    "Network_S",
    "Network_E",
    "Render_S",
    "Render_E",
    "GQL_Link_S",
    "GQL_Link_E",
    "GQL_Bridge_S",
    "GQL_Bridge_E",
    "GQL_Bridge_Error",
];

// small reusable numeric guard
const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export function getUnifiedPerfProperties(datapointProperties: CustomDataMap): UTCommonPerfProperties {
    if (!datapointProperties) {
        return {} as UTCommonPerfProperties;
    }
    
    const perfProperties = {} as UTCommonPerfProperties;
    
    // Extract predefined common properties
    if (Object.prototype.hasOwnProperty.call(datapointProperties, "E2ETimeElapsed") &&
        isFiniteNumber(datapointProperties.E2ETimeElapsed)) {
        perfProperties.E2ETimeElapsed = datapointProperties.E2ETimeElapsed;
    }
    if (Object.prototype.hasOwnProperty.call(datapointProperties, "UserPerceivedTime") &&
        isFiniteNumber(datapointProperties.UserPerceivedTime)) {
        perfProperties.UserPerceivedTime = datapointProperties.UserPerceivedTime;
    }
    if (Object.prototype.hasOwnProperty.call(datapointProperties, "Status")) {
        const rawStatus = datapointProperties.Status;
        if (typeof rawStatus === "string" &&
            Object.prototype.hasOwnProperty.call(mapDataPointStatusToUT, rawStatus)) {
            perfProperties.Status = mapDataPointStatusToUT[rawStatus as DatapointStatus];
        }
    }
    if (Object.prototype.hasOwnProperty.call(datapointProperties, "DataSource") &&
        typeof datapointProperties.DataSource === "string") {
        perfProperties.DataSource = datapointProperties.DataSource;
    }
    
    // Extract predefined checkpoints, logged as string numbers
    for (const checkpoint of checkpointValues) {
        const key = `WF_${checkpoint}`;
        if (Object.prototype.hasOwnProperty.call(datapointProperties, key)) {
            const val = Number(datapointProperties[key]);
            if (isFiniteNumber(val)) {
                (perfProperties as Record<string, number | undefined>)[key] = val;
            }
        }
    }
    
    return perfProperties;
}

const mapDataPointStatusToUT: {
    [key in DatapointStatus]: UTPerfDatapointStatus;
} = {
    Success: "Success",
    ServerError: "ServerError",
    ClientError: "ClientError",
    UserError: "UserError",
    UserCancelled: "UserCancelled",
    ServerExpectedError: "ServerExpectedError",
    RequestNotComplete: "RequestNotComplete",
    Timeout: "Timeout",
    BackgroundSuccess: "BackgroundSuccess",
    AuthError: "AuthError",
    PartnerError: "PartnerError",
    ServerClientError: "ServerClientError",
};
